<template>
  <a-modal
    :title="title"
    :visible="visible"
    :width="modalWidth"
    :mask-closable="true"
    :footer="null"
    @cancel="handleCancel"
    class="video-player-modal"
    :body-style="{ padding: 0, overflow: 'hidden' }"
    :destroy-on-close="true"

  >
    <!-- 视频容器 -->
    <div class="video-container" @click="handleVideoClick">
      <video
        ref="videoRef"
        :src="videoUrl"
        :poster="posterUrl"
        class="video-player"
        @loadedmetadata="handleLoadedMetadata"
        @timeupdate="handleTimeUpdate"
        @ended="handleVideoEnded"
        @error="handleVideoError"
      ></video>

      <!-- 视频控制层 -->
      <div class="video-controls" :class="{ 'controls-hidden': !showControls && !isDragging }">
        <!-- 进度条 -->
        <div
          class="progress-container"
          @click="handleProgressClick"
          @mousedown="startDragging"
        >
          <div
            class="progress-bar"
            :style="{ width: `${progressPercent}%` }"
          ></div>
          <div
            class="progress-handle"
            :style="{ left: `${progressPercent}%` }"
            :class="{ 'handle-active': isDragging || isHoveringHandle}"
            @mouseenter="isHoveringHandle = true"
            @mouseleave="isHoveringHandle = false"
          ></div>
        </div>

        <!-- 控制按钮区域 -->
        <div class="controls-bar">
          <div class="controls-left">
            <a-button
              icon="play-circle"
              type="dashed"
              class="control-btn"
              @click.stop="togglePlay"
            >
              <play-circle-o v-if="!isPlaying" />
              <pause-circle-o v-else />
            </a-button>

            <span class="time-display">
              {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
            </span>
          </div>

          <div class="controls-right">
            <div class="volume-slider" v-if="showVolumeSlider">
              <a-slider
                :value="volume * 100"
                @change="handleVolumeChange"
                @mousedown.stop
                @mouseup.stop
                class="volume-control"
              />
            </div>

            <a-button
              icon="fullscreen-exit"
              type="dashed"
              class="control-btn"
              @click.stop="toggleFullscreen"
            >
              <fullscreen />
            </a-button>

<!--            <a-button-->
<!--              type="text"-->
<!--              class="control-btn"-->
<!--              @click.stop="toggleSize"-->
<!--            >-->
<!--              <expand />-->
<!--            </a-button>-->
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-overlay">
      <a-spin size="large" />
    </div>

    <!-- 错误提示 -->
    <div v-if="hasError" class="error-overlay">
      <a-alert
        message="播放错误"
        description="无法加载视频，请检查视频URL是否正确。"
        type="error"
        show-icon
      />
      <a-button
        type="primary"
        style="margin-top: 16px"
        @click="handleRetry"
      >
        重试
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import {
  Modal,
  Button,
  Slider,
  Spin,
  Alert,
  PlayCircleOutlined,
  PauseCircleOutlined,
  VolumeUpOutlined,
  VolumeDownOutlined,
  VolumeOffOutlined,
  FullscreenOutlined,
  ExpandOutlined
} from 'ant-design-vue';

export default {
  name: 'VideoPlayerModal',
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-slider': Slider,
    'a-spin': Spin,
    'a-alert': Alert,
    PlayCircleOutlined,
    PauseCircleOutlined,
    VolumeUpOutlined,
    VolumeDownOutlined,
    VolumeOffOutlined,
    FullscreenOutlined,
    ExpandOutlined
  },
  props: {
    // 视频URL
    videoUrl: {
      type: String,
      required: true
    },
    // 视频封面图
    posterUrl: {
      type: String,
      default: ''
    },
    // 弹框标题
    title: {
      type: String,
      default: '视频播放'
    },
    // 是否显示弹框
    visible: {
      type: Boolean,
      default: false
    },
    // 初始音量
    initialVolume: {
      type: Number,
      default: 0.7,
      validator: function (value) {
        return value >= 0 && value <= 1;
      }
    },
    // 自动播放
    autoplay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 视频元素引用将通过 $refs 获取
      isPlaying: false,
      isMuted: false,
      volume: this.initialVolume,
      currentTime: 0,
      duration: 0,
      progressPercent: 0,
      isLoading: true,
      hasError: false,
      showControls: true,
      isDragging: false,
      isHoveringHandle: false,
      showVolumeSlider: true,
      modalWidth: 400,
      isFullscreen: false,
      controlsTimer: null,
      progressContainerRect: null  // 用于缓存进度条容器的尺寸信息
    };
  },
  watch: {
    // 监听可见性变化
    visible(newVal) {
      if (newVal && this.$refs.videoRef) {
        // 显示时重新加载视频
        this.$refs.videoRef.load();
        this.isLoading = true;
        this.hasError = false;
      }
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    // 监听窗口大小变化，更新进度条容器尺寸
    window.addEventListener('resize', this.updateProgressContainerRect);
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    window.removeEventListener('resize', this.updateProgressContainerRect);
    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
  },
  methods: {
    // 格式化时间（秒 -> MM:SS）
    formatTime(seconds) {
      if (isNaN(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    // 切换播放/暂停
    togglePlay() {
      const video = this.$refs.videoRef;
      if (!video) return;

      if (this.isPlaying) {
        video.pause();
      } else {
        video.play().catch(err => {
          console.error('播放失败:', err);
          // 处理自动播放限制
          this.isPlaying = false;
        });
      }
      this.isPlaying = !this.isPlaying;
      this.resetControlsTimer();
    },

    // 切换静音
    toggleMute() {
      const video = this.$refs.videoRef;
      if (!video) return;
      video.muted = !this.isMuted;
      this.isMuted = !this.isMuted;
      this.resetControlsTimer();
    },

    // 处理音量变化
    handleVolumeChange(value) {
      const video = this.$refs.videoRef;
      if (!video) return;
      const newVolume = value / 100;
      this.volume = newVolume;
      video.volume = newVolume;

      // 如果从静音状态调整音量，自动取消静音
      if (this.isMuted && newVolume > 0) {
        video.muted = false;
        this.isMuted = false;
      }
    },
// 更新进度条容器尺寸信息
    updateProgressContainerRect() {
      const container = this.$el.querySelector('.progress-container');
      if (container) {
        this.progressContainerRect = container.getBoundingClientRect();
      }
    },
    // 处理进度条点击
    handleProgressClick(e) {
      const video = this.$refs.videoRef;
      if (!video || this.isDragging) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const newTime = pos * this.duration;

      video.currentTime = newTime;
      this.currentTime = newTime;
      this.progressPercent = pos * 100;
      this.resetControlsTimer();
    },
// 开始拖动进度条
    startDragging(e) {
      const video = this.$refs.videoRef;
      if (!video || !this.duration) return;

      // 缓存进度条容器尺寸，避免在拖动过程中频繁计算
      this.updateProgressContainerRect();

      this.isDragging = true;
      // 暂停视频播放
      if (this.isPlaying) {
        video.pause();
        this.isPlaying = false;
      }
      this.handleProgressClick(e);

      // 使用箭头函数确保this指向正确
      const handleMouseMove = (moveEvent) => {
        if (!this.isDragging) return;
        // 防止拖动超出窗口时出现问题
        moveEvent.preventDefault();
        this.handleProgressClick(moveEvent);
      };

      const handleMouseUp = () => {
        if (this.isDragging) {
          this.isDragging = false;
          // 将视频定位到最终位置
          video.currentTime = this.currentTime;
          // 恢复播放状态
          if (!video.paused) {
            this.isPlaying = true;
          }
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseUp);
        this.resetControlsTimer();
      };

      // 添加鼠标移动和释放事件监听
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // 当鼠标离开文档时也处理释放事件
      document.addEventListener('mouseleave', handleMouseUp);

      this.resetControlsTimer();
    },

    // 切换全屏
    toggleFullscreen() {
      const video = this.$refs.videoRef;
      if (!video) return;

      const videoContainer = video.parentElement;

      if (!document.fullscreenElement) {
        videoContainer.requestFullscreen().catch(err => {
          console.error(`全屏错误: ${err.message}`);
        });
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
      this.resetControlsTimer();
    },

    // 切换弹框尺寸
    toggleSize() {
      this.modalWidth = this.modalWidth === 900 ? 1200 : 900;
      this.resetControlsTimer();
    },

    // 处理视频元数据加载完成
    handleLoadedMetadata() {
      const video = this.$refs.videoRef;
      if (!video) return;

      this.duration = video.duration;
      this.isLoading = false;

      // 应用初始音量
      video.volume = this.volume;
// 初始化进度条容器尺寸
      this.$nextTick(() => {
        this.updateProgressContainerRect();
      });
      // 自动播放
      if (this.autoplay) {
        this.togglePlay();
      }
    },

    // 处理视频时间更新
    handleTimeUpdate() {
      const video = this.$refs.videoRef;
      if (!video || this.isDragging) return;

      this.currentTime = video.currentTime;
      this.progressPercent = (this.currentTime / this.duration) * 100;
    },

    // 处理视频播放结束
    handleVideoEnded() {
      this.isPlaying = false;
      this.$emit('ended');
    },

    // 处理视频错误
    handleVideoError() {
      this.isLoading = false;
      this.hasError = true;
      this.isPlaying = false;
    },

    // 重试播放
    handleRetry() {
      this.hasError = false;
      this.isLoading = true;

      if (this.$refs.videoRef) {
        // 重新加载视频
        this.$refs.videoRef.load();
      }
    },

    // 关闭弹框
    handleCancel() {
      if (this.$refs.videoRef) {
        this.$refs.videoRef.pause();
        this.isPlaying = false;
      }
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    // 重置控制栏显示计时器
    resetControlsTimer() {
      this.showControls = true;

      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }

      // 视频播放时，5秒后隐藏控制栏
      if (this.isPlaying) {
        this.controlsTimer = setTimeout(() => {
          this.showControls = false;
        }, 5000);
      }
    },

    // 监听视频元素点击（播放/暂停）
    handleVideoClick() {
      this.togglePlay();
    },

    // 全屏变化事件监听
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
      // 全屏状态变化后更新进度条容器信息
      this.$nextTick(() => {
        this.updateProgressContainerRect();
      });
    }
  }
};
</script>

<style scoped>
.video-player-modal {
  .video-container {
    position: relative;
    width: 100%;
    background-color: #000;
    cursor: pointer;
  }

  .video-player {
    width: 100%;
    height: auto;
    display: block;
  }

  .video-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    color: #fff;
    transition: opacity 0.3s ease;
  }

  .controls-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .progress-container {
    position: relative;
    height: 4px;
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    margin-bottom: 12px;
    cursor: pointer;
  }

  .progress-bar {
    position: absolute;
    height: 100%;
    background-color: #1890ff;
    border-radius: 2px;
    transition: width 0.1s ease;
  }

  .progress-handle {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background-color: #fff;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .progress-container:hover .progress-handle {
    opacity: 1;
  }

  .handle-active {
    opacity: 1 !important;
    transform: translate(-50%, -50%) scale(1.2);
  }

  .controls-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .controls-left, .controls-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .time-display {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
  }

  .control-btn {
    color: #fff !important;
    font-size: 18px !important;
    padding: 4px 8px !important;
    transition: color 0.2s ease, transform 0.2s ease !important;
    height: 36px !important;
  }

  .control-btn:hover {
    color: #1890ff !important;
    transform: scale(1.1);
  }

  .volume-btn {
    position: relative;
  }

  .volume-slider {
    position: absolute;
    right: 64px;
    width: 80px;
    padding: 8px;
    border-radius: 4px;
    z-index: 10;
  }

  .volume-control {
    width: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  }

  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 10;
    padding: 24px;
    text-align: center;
  }
}
</style>
